import React from "react";
import { AriaTextFieldOptions, useTextField } from "react-aria";
import { Flex } from "../flex";
import { InputError } from "../input-error";
import { InputLabel } from "../input-label";
import { Text } from "../text/text";
import { useTheme } from "../theme";
import { FieldProps } from "../form/field";

interface TextareaProps
  extends AriaTextFieldOptions<"textarea">,
    FieldProps<"input"> {
  resize?: "none" | "both" | "horizontal" | "vertical";
  className?: string;
  style?: React.CSSProperties;
}

export const Textarea = React.forwardRef<HTMLDivElement, TextareaProps>(
  (
    {
      appearance = "outline",
      resize,
      label,
      description,
      error,
      color = "gray",
      style,
      radius,
      ...props
    },
    ref
  ) => {
    const textAreaRef = React.useRef(null);
    const { labelProps, inputProps, errorMessageProps, descriptionProps } =
      useTextField(
        {
          ...props,
          inputElementType: "textarea",
          label,
          description,
          errorMessage: error,
        },
        textAreaRef
      );

    const theme = useTheme();

    return (
      <Flex
        data-color={color}
        data-radius={radius ?? theme?.radius ?? "lg"}
        style={style}
        className="p-input-group"
        ref={ref}
        stack
        gap={"2xs"}
      >
        {label && <InputLabel {...labelProps}>{label}</InputLabel>}
        <textarea
          className="p-input-control p-text-area"
          disabled={props.isDisabled}
          data-has-error={!!error}
          data-resize={resize}
          data-appearance={appearance}
          {...inputProps}
        ></textarea>
        {error && <InputError {...errorMessageProps}>{error}</InputError>}
        {description && (
          <Text
            lineHeight={"base"}
            {...descriptionProps}
            variant={"light"}
            as="div"
            size={"sm"}
          >
            {description}
          </Text>
        )}
      </Flex>
    );
  }
);

Textarea.displayName = "Textarea";
